import { animate, MotionValue, useMotionValue } from "framer-motion";
import { PointerEvent, useCallback, useRef } from "react";
import { IOS_EASE_BEZIER } from "./modalConstants";

const DISMISS_DISTANCE_PX = 100;
const DISMISS_VELOCITY_PX_PER_MS = 0.5;
const SPRING_BACK_DURATION = 0.3;

interface UseSheetDragArgs {
  onDismiss: () => void;
}

interface UseSheetDragResult {
  /**
   * MotionValue carrying the live drag offset (px). Pass directly to the
   * sheet's `style={{ y: dragY }}` — framer-motion drives it as a motion
   * value, so variant animations on the same axis remain untouched while the
   * user isn't dragging.
   */
  dragY: MotionValue<number>;
  handlePointerDown: (e: PointerEvent<HTMLElement>) => void;
}

/**
 * Pointer-driven drag-down-to-dismiss for the sheet handle. Tracks vertical
 * offset during drag and on release either dismisses or springs back to 0.
 */
export function useSheetDrag({
  onDismiss
}: UseSheetDragArgs): UseSheetDragResult {
  const dragY = useMotionValue(0);
  const startYRef = useRef(0);
  const startTimeRef = useRef(0);
  const lastYRef = useRef(0);
  const lastTimeRef = useRef(0);

  const handlePointerMove = useCallback(
    (e: globalThis.PointerEvent) => {
      const dy = e.clientY - startYRef.current;
      const clamped = Math.max(0, dy);
      lastYRef.current = e.clientY;
      lastTimeRef.current = performance.now();
      dragY.set(clamped);
    },
    [dragY]
  );

  const handlePointerUp = useCallback(() => {
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);

    const dy = lastYRef.current - startYRef.current;
    const dt = lastTimeRef.current - startTimeRef.current;
    const velocity = dt > 0 ? dy / dt : 0;

    const shouldDismiss =
      dy > DISMISS_DISTANCE_PX || velocity > DISMISS_VELOCITY_PX_PER_MS;

    if (shouldDismiss) {
      onDismiss();
      return;
    }
    animate(dragY, 0, {
      duration: SPRING_BACK_DURATION,
      ease: IOS_EASE_BEZIER
    });
  }, [dragY, handlePointerMove, onDismiss]);

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      dragY.stop();
      startYRef.current = e.clientY;
      lastYRef.current = e.clientY;
      startTimeRef.current = performance.now();
      lastTimeRef.current = startTimeRef.current;
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [dragY, handlePointerMove, handlePointerUp]
  );

  return { dragY, handlePointerDown };
}
